import { Button, Modal, Select, Space, Tooltip } from "antd";
import {
  CheckCircleFilled,
  CloudDownloadOutlined,
  CopyOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Table } from "antd";
import { useEffect, useState } from "react";
import "./ApiKeys.css";

import { TopBar } from "../../../../components/widgets/top-bar/TopBar";
import { useAxiosPrivate } from "../../../../hooks/useAxiosPrivate";
import { useSessionStore } from "../../../../store/session-store";
import { formattedDateTime } from "../../../../helpers/GetStaticData";
import { useAlertStore } from "../../../../store/alert-store";
import { useExceptionHandler } from "../../../../hooks/useExceptionHandler";
import { extractPerUnitPrices, LLM_WHISPERER_ENDPOINT } from "../../helper.js";
import { ConfirmModal } from "../../../../components/widgets/confirm-modal/ConfirmModal.jsx";
import { SpinnerLoader } from "../../../../components/widgets/spinner-loader/SpinnerLoader.jsx";
import { IslandLayout } from "../../../../layouts/island-layout/IslandLayout.jsx";
import { SupportComponent } from "../support-components/SupportComponent.jsx";

function ApiKeys() {
  const axiosPrivate = useAxiosPrivate();
  const { sessionDetails } = useSessionStore();
  const { setAlertDetails } = useAlertStore();
  const handleException = useExceptionHandler();
  const [apiKeys, setApiKeys] = useState([]);
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingRotate, setLoadingRotate] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const keysEndpoint = `${LLM_WHISPERER_ENDPOINT}/keys/`;

  const getApiKeys = () => {
    const requestOptions = {
      method: "GET",
      url: keysEndpoint,
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
      },
    };
    setLoadingTable(true);
    axiosPrivate(requestOptions)
      .then((res) => {
        const keys = res?.data;
        if (keys?.api_keys) {
          setApiKeys(keys?.api_keys);
        }
      })
      .catch((err) => {
        setAlertDetails(handleException(err, "Failed to fetch API Keys"));
      })
      .finally(() => {
        setLoadingTable(false);
      });
  };

  const deleteApiKeys = (apiKey) => {
    const requestOptions = {
      method: "DELETE",
      url: keysEndpoint,
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
      },
      data: {
        key_id: apiKey,
      },
    };
    setLoadingDelete(true);
    axiosPrivate(requestOptions)
      .then(() => {
        setApiKeys((prevKeys) =>
          prevKeys.filter((key) => key?.api_key !== apiKey)
        );
        setAlertDetails({
          type: "success",
          content: "API Key deleted successfully",
        });
      })
      .catch((err) => {
        setAlertDetails(handleException(err, "Failed to delete API Key"));
      })
      .finally(() => setLoadingDelete(false));
  };

  const generateApiKey = () => {
    if (!selectedProduct) {
      setAlertDetails({
        type: "error",
        content: "Please select a product.",
      });
      return;
    }

    const isNewKey = apiKeys?.some(
      (obj) => obj?.product?.id === selectedProduct
    );

    if (isNewKey) {
      setAlertDetails({
        type: "error",
        content: "Key already available for this plan.",
      });
      return;
    }

    const requestOptions = {
      method: "POST",
      url: keysEndpoint,
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
      },
      data: {
        product_id: selectedProduct,
      },
    };
    setLoadingGenerate(true);
    axiosPrivate(requestOptions)
      .then(() => {
        setAlertDetails({
          type: "success",
          content: "API Key generated successfully",
        });
        getApiKeys();
        setModalVisible(false);
        setSelectedProduct(null);
      })
      .catch((err) => {
        setAlertDetails({
          type: "error",
          content: err?.response?.data?.message,
        });
      })
      .finally(() => setLoadingGenerate(false));
  };

  const rotateApiKey = (apiKey) => {
    const requestOptions = {
      method: "PUT",
      url: keysEndpoint,
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
      },
      data: {
        api_key: apiKey,
      },
    };
    setLoadingRotate(true);
    axiosPrivate(requestOptions)
      .then(() => {
        setAlertDetails({
          type: "success",
          content: "API Key rotated successfully",
        });
        getApiKeys();
      })
      .catch((err) => {
        handleException(err);
      })
      .finally(() => setLoadingRotate(false));
  };

  const fetchProducts = () => {
    setLoadingProducts(true);
    axiosPrivate({
      method: "GET",
      url: `${LLM_WHISPERER_ENDPOINT}/pricing`,
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
      },
    })
      .then((response) => {
        const productsList = response?.data || [];
        setProducts(extractPerUnitPrices(productsList));
      })
      .catch((err) => {
        handleException(err);
      })
      .finally(() => setLoadingProducts(false));
  };

  const openGenerateModal = () => {
    setModalVisible(true);
  };
  const handleChange = (value) => {
    setSelectedProduct(value);
  };

  const copyText = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setAlertDetails({
          type: "success",
          content: "API key copied to clipboard",
        });
      })
      .catch((error) => {
        setAlertDetails({
          type: "error",
          content: "Copy failed",
        });
      });
  };

  const cancelGenerateKey = () => {
    setModalVisible(false);
    setSelectedProduct(null);
  };

  const getSubscribedProductOptions = (products) => {
    return products
      .filter((product) => product?.isSubscribed)
      .map((product) => ({
        value: product?.product,
        label: product?.name,
      }));
  };

  const downloadAsPostmanCollect = (id) => {
    setLoadingDownload(true);
    const options = {
      method: "GET",
      url: `${keysEndpoint}postman_collection/${id}/`,
      responseType: "blob",
    };
    axiosPrivate(options)
      .then((res) => {
        const { data, headers } = res;
        const href = URL.createObjectURL(data);
        // Get filename from header or use a default
        const filename =
          headers["content-disposition"]
            ?.split("filename=")[1]
            ?.trim()
            .replaceAll('"', "") || "postman_collection.json";
        // create "a" HTML element with href to file & click
        const link = document.createElement("a");
        link.href = href;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();

        // clean up "a" element & remove ObjectURL
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
        setAlertDetails({
          type: "success",
          content: "Collection downloaded successfully",
        });
      })
      .catch((err) => {
        setAlertDetails(handleException(err));
      })
      .finally(() => {
        setLoadingDownload(false);
      });
  };

  const columns = [
    {
      title: "Keys",
      dataIndex: "api_key",
      key: "api_key",
    },
    {
      title: "Plan",
      dataIndex: ["product", "name"],
      key: "product_name",
    },
    {
      title: "Generated At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => formattedDateTime(date),
    },
    {
      title: "State",
      dataIndex: "is_active",
      key: "is_active",
      render: (text) => (
        <>
          <CheckCircleFilled className="api-key-checked-icon" />{" "}
          {text ? "Active" : "Inactive"}
        </>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <Space>
          <Tooltip title={"Copy key"}>
            <Button
              onClick={() => {
                copyText(record.api_key);
              }}
              icon={<CopyOutlined />}
              size="small"
            />
          </Tooltip>
          <Tooltip title={"Download Postman Collection"}>
            <Button
              onClick={() => downloadAsPostmanCollect(record.api_key)}
              icon={<CloudDownloadOutlined />}
              size="small"
              loading={loadingDownload}
            />
          </Tooltip>
          {record.product.id !== "free" && (
            <>
              <Tooltip title={"Rotate key"}>
                <Button
                  onClick={() => rotateApiKey(record.api_key)}
                  icon={<ReloadOutlined />}
                  size="small"
                  loading={loadingRotate}
                />
              </Tooltip>
              <ConfirmModal
                handleConfirm={() => deleteApiKeys(record.api_key)}
                content="Want to delete this key"
              >
                <Tooltip title={"Delete"}>
                  <Button
                    icon={<DeleteOutlined />}
                    size="small"
                    disabled={loadingDelete}
                    loading={loadingDelete}
                  />
                </Tooltip>
              </ConfirmModal>
            </>
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    getApiKeys();
    fetchProducts();
  }, []);
  return (
    <>
      <TopBar title="API Keys">
        <SupportComponent />
        <Button
          type="primary"
          onClick={openGenerateModal}
          loading={loadingGenerate}
          disabled={
            !products.some(
              (product) => product?.isSubscribed && !product.isFreePlan
            )
          }
        >
          Generate API Key
        </Button>
      </TopBar>
      <div className="user-bg-col">
        <IslandLayout>
          <div className="user-table">
            <Table
              dataSource={apiKeys}
              columns={columns}
              pagination={false}
              showSorterTooltip={{ target: "sorter-icon" }}
              size="small"
              loading={{
                indicator: (
                  <div>
                    <SpinnerLoader />
                  </div>
                ),
                spinning: loadingTable,
              }}
            />
          </div>
        </IslandLayout>
      </div>
      <Modal
        title="Generate API Key"
        open={modalVisible}
        onCancel={cancelGenerateKey}
        footer={[
          <Button key="back" onClick={cancelGenerateKey}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={generateApiKey}
            loading={loadingGenerate}
          >
            Generate Key
          </Button>,
        ]}
      >
        <Select
          className="api-key-select"
          placeholder="Select a product"
          onChange={handleChange}
          options={getSubscribedProductOptions(products)}
          value={selectedProduct}
          loading={loadingProducts}
          disabled={loadingProducts}
        />
      </Modal>
    </>
  );
}

export { ApiKeys };
